import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { fb } from '../../../../../shared/shared.global-service';
import { Validators } from '@angular/forms';
import { FolderRiskStatusObject } from '@libs/types/src/services/object-type/folder/folder-risk-status.object';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { PersonRisksStore } from '../person-risks.store';
import {
    FolderRiskStatusCreateInput,
} from '@libs/types/src/services/input-type/folder/folder-risk-status.create-input';
import { contentGlobalSuccess } from '../../../../content.error';
import {
    FolderRiskStatusUpdateInput,
} from '@libs/types/src/services/input-type/folder/folder-risk-status.update-input';
import { folderRiskStatusEnumOptions } from '@libs/types/src/services/enum/folder-risk-status.enum';
import { FilterBuilder } from '../../../../helper/filter.builder';
import {
    SERVICE_RISK_TYPE_DEFAULT,
    SERVICE_RISK_TYPE_KEY,
} from '@libs/types/src/services/types/service-settings-risk-type.type';
import { BaseDialogComponent } from '../../../../../../components/kit/base-dialog/base-dialog.component';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';

@Component({
    selector: 'app-person-risks-dialog',
    templateUrl: './person-risks-dialog.component.html',
    styleUrl: './person-risks-dialog.component.scss',
    providers: [PersonRisksStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        SelectInputComponent,
        TextInputComponent,
    ],
})
export class PersonRisksDialogComponent extends BaseDialogComponent implements OnInit {
    protected readonly store = inject(PersonRisksStore);

    protected form = fb.group({
        dateFrom: fb.control<Date>(new Date(), [Validators.required]),
        status: fb.control('', [Validators.required]),
        riskType: fb.control('', [Validators.required]),
        note: fb.control(''),
    });

    riskTypeOptions = SERVICE_RISK_TYPE_DEFAULT.riskTypes.map((type: string) => ({ name: type }));

    risk: FolderRiskStatusObject | null = null;

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.store.api.getServiceSettings(FilterBuilder.singlePage({
            serviceId: this.store.serviceId(),
            key: SERVICE_RISK_TYPE_KEY,
        })).then(() => this.setRiskTypesOptions());

        this.risk = this.dialogConfig.data.risk;
        if (this.risk) {
            this.form.patchValue({
                ...this.risk,
                dateFrom: new Date(this.risk.dateFrom),
            });
        }
    }

    override async save() {
        const folderId = this.store.folderId();
        if (isInvalid(this.form) || !folderId) return;

        if (!this.risk) {
            const data = {
                ...this.form.value,
                folderId: folderId,
                dateFrom: this.form.value.dateFrom!.toISOString(),
            } as FolderRiskStatusCreateInput;
            await this.store.api.createFolderRiskStatus(data);
            contentGlobalSuccess.next('common.created');
        } else {
            const data = {
                id: this.risk.id,
                ...this.form.value,
                dateFrom: this.form.value.dateFrom!.toISOString(),
            } as FolderRiskStatusUpdateInput;
            await this.store.api.updateFolderRiskStatus(data);
            contentGlobalSuccess.next('common.updated');
        }
        this.dialogRef.close();
    }

    private setRiskTypesOptions() {
        if (!this.store.riskTypes().length) return;
        this.riskTypeOptions = this.store.riskTypes().map((type: string) => ({ name: type }));
    }

    protected readonly folderRiskStatusEnumOptions = folderRiskStatusEnumOptions;
}
